import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { 
    FloppyDisk, X, PencilSimple, Eraser, ArrowCounterClockwise,
    ArrowRight, Circle, Square, Minus, TextT, Car
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#FFFFFF', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#D4A537'];
const WIDTHS = [2, 4, 6, 10];
const TOOLS = [
    { id: 'pen', name: 'Pen', icon: PencilSimple },
    { id: 'line', name: 'Lijn', icon: Minus },
    { id: 'arrow', name: 'Pijl', icon: ArrowRight },
    { id: 'rect', name: 'Vak', icon: Square },
    { id: 'circle', name: 'Cirkel', icon: Circle },
    { id: 'text', name: 'Tekst', icon: TextT },
    { id: 'eraser', name: 'Gum', icon: Eraser },
];
const BACKGROUNDS = [
    { id: 'leeg', name: 'Leeg' },
    { id: 'kruispunt', name: 'Kruispunt' },
    { id: 'rotonde', name: 'Rotonde' },
];
const AUTO_COLORS = ['#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#FFFFFF', '#D4A537'];

function RoadBg({ type, w, h }) {
    const road = '#3D3D3D', roadL = '#4A4A4A', sw = '#6B6B6B', grass = '#2D5A27', mk = '#E8E8E8', gold = '#D4A537';
    if (type === 'kruispunt') return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full pointer-events-none">
            <rect width={w} height={h} fill={grass}/>
            <rect x={0} y={h*.3} width={w} height={h*.4} fill={road}/>
            <rect x={w*.3} y={0} width={w*.4} height={h} fill={road}/>
            <rect x={w*.3} y={h*.3} width={w*.4} height={h*.4} fill={roadL}/>
            <rect x={w*.28} y={0} width={w*.02} height={h*.28} fill={sw}/><rect x={w*.7} y={0} width={w*.02} height={h*.28} fill={sw}/>
            <rect x={w*.28} y={h*.72} width={w*.02} height={h*.28} fill={sw}/><rect x={w*.7} y={h*.72} width={w*.02} height={h*.28} fill={sw}/>
            <rect x={0} y={h*.28} width={w*.28} height={h*.02} fill={sw}/><rect x={0} y={h*.7} width={w*.28} height={h*.02} fill={sw}/>
            <rect x={w*.72} y={h*.28} width={w*.28} height={h*.02} fill={sw}/><rect x={w*.72} y={h*.7} width={w*.28} height={h*.02} fill={sw}/>
            <line x1={w*.5} y1={0} x2={w*.5} y2={h*.28} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={w*.5} y1={h*.72} x2={w*.5} y2={h} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={0} y1={h*.5} x2={w*.28} y2={h*.5} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={w*.72} y1={h*.5} x2={w} y2={h*.5} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={w*.3} y1={h*.31} x2={w*.48} y2={h*.31} stroke={mk} strokeWidth="3"/>
            <line x1={w*.52} y1={h*.69} x2={w*.7} y2={h*.69} stroke={mk} strokeWidth="3"/>
        </svg>
    );
    if (type === 'rotonde') return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 w-full h-full pointer-events-none">
            <rect width={w} height={h} fill={grass}/>
            <rect x={w*.38} y={0} width={w*.24} height={h} fill={road}/>
            <rect x={0} y={h*.38} width={w} height={h*.24} fill={road}/>
            <circle cx={w*.5} cy={h*.5} r={w*.25} fill={road}/>
            <circle cx={w*.5} cy={h*.5} r={w*.12} fill={grass}/>
            <circle cx={w*.5} cy={h*.5} r={w*.13} fill="none" stroke={sw} strokeWidth="3"/>
            <circle cx={w*.5} cy={h*.5} r={w*.19} fill="none" stroke={mk} strokeWidth="1.5" strokeDasharray="10,8"/>
            <line x1={w*.5} y1={0} x2={w*.5} y2={h*.25} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={w*.5} y1={h*.75} x2={w*.5} y2={h} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={0} y1={h*.5} x2={w*.25} y2={h*.5} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <line x1={w*.75} y1={h*.5} x2={w} y2={h*.5} stroke={mk} strokeWidth="2" strokeDasharray="12,8"/>
            <text x={w*.5} y={h*.32} textAnchor="middle" fill={gold} fontSize="14" fontWeight="bold">→</text>
            <text x={w*.68} y={h*.52} textAnchor="middle" fill={gold} fontSize="14" fontWeight="bold">↓</text>
            <text x={w*.5} y={h*.71} textAnchor="middle" fill={gold} fontSize="14" fontWeight="bold">←</text>
            <text x={w*.32} y={h*.52} textAnchor="middle" fill={gold} fontSize="14" fontWeight="bold">↑</text>
        </svg>
    );
    return null;
}

function DraggableCar({ car, onMove, onRotate, onDelete, selected, onSelect, containerRef }) {
    const [dragging, setDragging] = useState(false);
    const [off, setOff] = useState({ x: 0, y: 0 });
    const ref = useRef(null);
    const start = (cx, cy) => {
        const r = ref.current.getBoundingClientRect();
        setOff({ x: cx - r.left, y: cy - r.top });
        setDragging(true);
        onSelect();
    };
    const move = useCallback((cx, cy) => {
        if (!dragging || !containerRef?.current) return;
        const p = containerRef.current.getBoundingClientRect();
        onMove(cx - p.left - off.x, cy - p.top - off.y);
    }, [dragging, off, onMove, containerRef]);
    useEffect(() => {
        if (!dragging) return;
        const mm = (e) => move(e.clientX, e.clientY);
        const tm = (e) => { e.preventDefault(); move(e.touches[0].clientX, e.touches[0].clientY); };
        const end = () => setDragging(false);
        window.addEventListener('mousemove', mm); window.addEventListener('touchmove', tm, { passive: false });
        window.addEventListener('mouseup', end); window.addEventListener('touchend', end);
        return () => { window.removeEventListener('mousemove', mm); window.removeEventListener('touchmove', tm); window.removeEventListener('mouseup', end); window.removeEventListener('touchend', end); };
    }, [dragging, move]);

    return (
        <div ref={ref} className="absolute z-20 cursor-grab active:cursor-grabbing select-none"
            style={{ left: `${car.x}%`, top: `${car.y}%`, transform: `translate(-50%,-50%) rotate(${car.rotation||0}deg)` }}
            onMouseDown={(e) => { e.stopPropagation(); start(e.clientX, e.clientY); }}
            onTouchStart={(e) => { e.stopPropagation(); start(e.touches[0].clientX, e.touches[0].clientY); }}>
            <svg width="44" height="24" viewBox="0 0 36 20" className="drop-shadow-md">
                <rect x="1" y="4" width="34" height="12" rx="3" fill={car.color}/>
                <rect x="6" y="1" width="12" height="10" rx="2" fill={car.color} opacity="0.7"/>
                <rect x="8" y="2" width="8" height="6" rx="1" fill="#87CEEB" opacity="0.6"/>
                <circle cx="9" cy="17" r="3" fill="#333"/><circle cx="27" cy="17" r="3" fill="#333"/>
                <circle cx="9" cy="17" r="1.5" fill="#666"/><circle cx="27" cy="17" r="1.5" fill="#666"/>
            </svg>
            {selected && (
                <div className="absolute -top-9 left-1/2 -translate-x-1/2 flex gap-1.5">
                    <button onClick={(e) => { e.stopPropagation(); onRotate((car.rotation||0)+45); }} className="w-7 h-7 bg-[#D4A537] rounded-full text-sm text-[#1A202C] font-bold shadow-lg">↻</button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="w-7 h-7 bg-red-500 rounded-full text-sm text-white font-bold shadow-lg">✕</button>
                </div>
            )}
        </div>
    );
}

// ========== MAIN COMPONENT ==========
export default function TrafficSketchTool({ studentId, onClose, onSave }) {
    const [title, setTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const [bg, setBg] = useState('kruispunt');
    const [tool, setTool] = useState('pen');
    const [color, setColor] = useState('#FFFFFF');
    const [penWidth, setPenWidth] = useState(4);
    const [strokes, setStrokes] = useState([]);
    const [undone, setUndone] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [curStroke, setCurStroke] = useState(null);
    const [textInput, setTextInput] = useState(null);
    const [cars, setCars] = useState([]);
    const [selCar, setSelCar] = useState(null);
    const [carColor, setCarColor] = useState('#EF4444');
    const [showColors, setShowColors] = useState(false);
    const [saved, setSaved] = useState(false);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const CW = 800, CH = 500;

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, CW, CH);
        if (bg === 'leeg') { ctx.fillStyle = '#1e293b'; ctx.fillRect(0, 0, CW, CH); }
        const all = curStroke ? [...strokes, curStroke] : strokes;
        all.forEach(s => {
            if (s.tool === 'text') { ctx.font = `${s.size*4}px sans-serif`; ctx.fillStyle = s.color; ctx.fillText(s.text, s.x, s.y); return; }
            ctx.strokeStyle = s.tool === 'eraser' ? (bg === 'leeg' ? '#1e293b' : '#2D5A27') : s.color;
            ctx.lineWidth = s.tool === 'eraser' ? s.width*3 : s.width;
            ctx.lineCap = 'round'; ctx.lineJoin = 'round';
            if (s.tool === 'pen' || s.tool === 'eraser') {
                if (s.points.length < 2) return;
                ctx.beginPath(); ctx.moveTo(s.points[0].x, s.points[0].y);
                s.points.forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke();
            } else if (s.tool === 'line') { ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke(); }
            else if (s.tool === 'arrow') {
                const a = Math.atan2(s.y2-s.y1, s.x2-s.x1), hl = 15;
                ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(s.x2, s.y2);
                ctx.lineTo(s.x2-hl*Math.cos(a-Math.PI/6), s.y2-hl*Math.sin(a-Math.PI/6));
                ctx.moveTo(s.x2, s.y2);
                ctx.lineTo(s.x2-hl*Math.cos(a+Math.PI/6), s.y2-hl*Math.sin(a+Math.PI/6)); ctx.stroke();
            } else if (s.tool === 'rect') { ctx.strokeRect(s.x1, s.y1, s.x2-s.x1, s.y2-s.y1); }
            else if (s.tool === 'circle') {
                const rx = Math.abs(s.x2-s.x1)/2, ry = Math.abs(s.y2-s.y1)/2;
                ctx.beginPath(); ctx.ellipse((s.x1+s.x2)/2, (s.y1+s.y2)/2, rx||1, ry||1, 0, 0, Math.PI*2); ctx.stroke();
            }
        });
    }, [strokes, curStroke, bg]);

    const getPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const cx = e.touches ? e.touches[0].clientX : e.clientX;
        const cy = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: (cx - rect.left) * (CW / rect.width), y: (cy - rect.top) * (CH / rect.height) };
    };

    const handleStart = (e) => {
        if (tool === 'car') return;
        e.preventDefault(); e.stopPropagation(); setSelCar(null);
        const pos = getPos(e);
        if (tool === 'text') { setTextInput(pos); return; }
        setIsDrawing(true); setUndone([]);
        if (tool === 'pen' || tool === 'eraser') setCurStroke({ tool, color, width: penWidth, points: [pos] });
        else setCurStroke({ tool, color, width: penWidth, x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y });
    };
    const handleMove = (e) => {
        if (!isDrawing || !curStroke) return; 
        e.preventDefault(); e.stopPropagation();
        const pos = getPos(e);
        if (curStroke.tool === 'pen' || curStroke.tool === 'eraser') setCurStroke(prev => prev ? ({ ...prev, points: [...prev.points, pos] }) : null);
        else setCurStroke(prev => prev ? ({ ...prev, x2: pos.x, y2: pos.y }) : null);
    };
    const handleEnd = (e) => { 
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (curStroke) {
            setStrokes(prev => [...prev, curStroke]); 
        }
        setIsDrawing(false); 
        setCurStroke(null); 
    };
    const addText = (text) => { if (text && textInput) setStrokes(prev => [...prev, { tool: 'text', color, size: penWidth, text, x: textInput.x, y: textInput.y }]); setTextInput(null); };
    const undo = () => { if (!strokes.length) return; setUndone(prev => [...prev, strokes.at(-1)]); setStrokes(prev => prev.slice(0, -1)); };
    const redo = () => { if (!undone.length) return; setStrokes(prev => [...prev, undone.at(-1)]); setUndone(prev => prev.slice(0, -1)); };

    const addCar = () => setCars(prev => [...prev, { id: Date.now().toString(), color: carColor, x: 50, y: 50, rotation: 0 }]);

    const moveCar = (id, cx, cy) => {
        const c = containerRef.current;
        if (!c) return;
        const r = c.getBoundingClientRect();
        const px = ((cx) / r.width) * 100;
        const py = ((cy) / r.height) * 100;
        setCars(prev => prev.map(car => car.id === id ? { ...car, x: Math.max(0, Math.min(100, px)), y: Math.max(0, Math.min(100, py)) } : car));
    };

    const handleSave = async () => {
        if (strokes.length === 0 && cars.length === 0) {
            setSaved(false);
            return;
        }
        setSaving(true);
        try {
            // Simplify strokes - reduce points for large pen strokes
            const simplifiedStrokes = strokes.map(s => {
                if ((s.tool === 'pen' || s.tool === 'eraser') && s.points && s.points.length > 100) {
                    // Keep every nth point for large strokes
                    const nth = Math.ceil(s.points.length / 100);
                    return { ...s, points: s.points.filter((_, i) => i % nth === 0 || i === s.points.length - 1) };
                }
                return s;
            });

            await axios.post(`${API}/sketches`, {
                student_id: studentId, background: bg, elements: cars, lines: simplifiedStrokes,
                app_state: {}, title: title || undefined
            }, { withCredentials: true });
            if (onSave) onSave();
            // Reset canvas for new sketch
            setStrokes([]);
            setUndone([]);
            setCars([]);
            setTitle('');
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) { console.error('Save error:', e); } finally { setSaving(false); }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-[98vw] max-w-[1100px] bg-[#0f172a] border-[#334155] text-white p-0 max-h-[98vh] overflow-y-auto">
                {/* Header - responsive */}
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-[#334155] sticky top-0 z-40 bg-[#0f172a]">
                    <DialogTitle className="text-sm sm:text-base font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Situatieschets</DialogTitle>
                    <div className="flex items-center gap-2">
                        <Input placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)}
                            className="w-28 sm:w-44 h-8 bg-[#1e293b] border-[#334155] text-white text-xs sm:text-sm placeholder:text-gray-500" />
                        <Button size="sm" onClick={handleSave} disabled={saving || (strokes.length === 0 && cars.length === 0)}
                            className="h-8 bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold text-xs sm:text-sm disabled:opacity-50" data-testid="save-sketch-button">
                            <FloppyDisk size={16} className="mr-1" /> {saving ? '...' : saved ? 'Opgeslagen!' : `Opslaan (${strokes.length})`}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={onClose} className="h-8 text-gray-400 hover:text-white w-8 p-0"><X size={18} /></Button>
                    </div>
                </div>

                {/* Toolbar - 2 rows on mobile */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-[#334155] bg-[#1e293b]">
                    {/* Row 1: Backgrounds + Tools */}
                    <div className="flex gap-1 bg-[#0f172a] rounded-lg p-0.5">
                        {BACKGROUNDS.map(b => (
                            <button key={b.id} onClick={() => setBg(b.id)}
                                className={`px-2 py-1.5 rounded text-[11px] sm:text-xs ${bg === b.id ? 'bg-[#D4A537] text-[#1A202C] font-bold' : 'text-gray-400 hover:text-white'}`}>
                                {b.name}
                            </button>
                        ))}
                    </div>

                    <div className="flex gap-0.5 bg-[#0f172a] rounded-lg p-0.5">
                        {TOOLS.map(t => {
                            const Icon = t.icon;
                            return (<button key={t.id} onClick={() => setTool(t.id)} title={t.name}
                                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded ${tool === t.id ? 'bg-[#D4A537] text-[#1A202C]' : 'text-gray-400 hover:text-white hover:bg-[#334155]'}`}>
                                <Icon size={16} weight={tool === t.id ? 'bold' : 'regular'} />
                            </button>);
                        })}
                    </div>

                    {/* Colors toggle on mobile, inline on desktop */}
                    <button onClick={() => setShowColors(!showColors)} className="sm:hidden w-8 h-8 rounded-full border-2 border-white" style={{ backgroundColor: color }} />
                    <div className={`${showColors ? 'flex' : 'hidden'} sm:flex gap-1`}>
                        {COLORS.map(c => (
                            <button key={c} onClick={() => { setColor(c); setShowColors(false); }}
                                className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 transition-all ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                                style={{ backgroundColor: c }} />
                        ))}
                    </div>

                    {/* Widths */}
                    <div className="flex gap-0.5">
                        {WIDTHS.map(w2 => (
                            <button key={w2} onClick={() => setPenWidth(w2)}
                                className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded ${penWidth === w2 ? 'bg-[#D4A537] text-[#1A202C]' : 'text-gray-400 hover:bg-[#334155]'}`}>
                                <div className="rounded-full bg-current" style={{ width: w2+2, height: w2+2 }} />
                            </button>
                        ))}
                    </div>

                    {/* Car */}
                    <div className="flex items-center gap-1 bg-[#0f172a] rounded-lg p-0.5">
                        {AUTO_COLORS.map(c => (
                            <button key={c} onClick={() => setCarColor(c)}
                                className={`w-5 h-5 rounded border ${carColor === c ? 'border-white' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                        ))}
                        <button onClick={addCar} className="flex items-center gap-1 px-2 py-1.5 rounded bg-[#D4A537] text-[#1A202C] text-[11px] sm:text-xs font-bold" data-testid="add-car-button">
                            <Car size={14} weight="fill" /> Auto
                        </button>
                    </div>

                    {/* Undo/Redo */}
                    <div className="flex gap-0.5">
                        <button onClick={undo} disabled={!strokes.length} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white disabled:opacity-30"><ArrowCounterClockwise size={16} /></button>
                        <button onClick={redo} disabled={!undone.length} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-white disabled:opacity-30 scale-x-[-1]"><ArrowCounterClockwise size={16} /></button>
                        <button onClick={() => { setStrokes([]); setUndone([]); setCars([]); }} className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-400"><X size={16} /></button>
                    </div>
                </div>

                {/* Canvas - fills available space, responsive aspect ratio */}
                <div className="p-2 sm:p-3 bg-[#0f172a]">
                    <div ref={containerRef} className="relative rounded-lg overflow-hidden border border-[#334155] mx-auto" style={{ width: '100%', maxWidth: CW, aspectRatio: `${CW}/${CH}` }}>
                        <RoadBg type={bg} w={CW} h={CH} />
                        <canvas ref={canvasRef} width={CW} height={CH}
                            className="absolute inset-0 z-10 w-full h-full"
                            style={{ cursor: tool === 'text' ? 'text' : tool === 'eraser' ? 'cell' : 'crosshair', touchAction: 'none' }}
                            onMouseDown={handleStart} onMouseMove={handleMove} onMouseUp={handleEnd} onMouseLeave={handleEnd}
                            onTouchStart={handleStart} onTouchMove={handleMove} onTouchEnd={handleEnd} />
                        {cars.map(car => (
                            <DraggableCar key={car.id} car={car} containerRef={containerRef} selected={selCar === car.id}
                                onSelect={() => setSelCar(car.id)}
                                onMove={(x, y) => moveCar(car.id, x, y)}
                                onRotate={(r) => setCars(prev => prev.map(c => c.id === car.id ? { ...c, rotation: r } : c))}
                                onDelete={() => { setCars(prev => prev.filter(c => c.id !== car.id)); setSelCar(null); }} />
                        ))}
                        {textInput && (
                            <div className="absolute z-30" style={{ left: `${(textInput.x/CW)*100}%`, top: `${(textInput.y/CH)*100}%` }}>
                                <input autoFocus className="bg-transparent border-b-2 text-white outline-none text-base sm:text-lg px-1 w-32 sm:w-auto"
                                    style={{ borderColor: color, color }} placeholder="Typ..."
                                    onKeyDown={(e) => { if (e.key === 'Enter') addText(e.target.value); if (e.key === 'Escape') setTextInput(null); }}
                                    onBlur={(e) => addText(e.target.value)} />
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ========== VIEWER ==========
export function SketchViewer({ sketch, onClose }) {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, 800, 500);
        if (sketch.background === 'leeg') { ctx.fillStyle = '#1e293b'; ctx.fillRect(0, 0, 800, 500); }
        (sketch.lines || []).forEach(s => {
            if (s.tool === 'text') { ctx.font = `${s.size*4}px sans-serif`; ctx.fillStyle = s.color; ctx.fillText(s.text, s.x, s.y); return; }
            ctx.strokeStyle = s.tool === 'eraser' ? '#2D5A27' : s.color; ctx.lineWidth = s.tool === 'eraser' ? s.width*3 : s.width;
            ctx.lineCap = 'round'; ctx.lineJoin = 'round';
            if (s.tool === 'pen' || s.tool === 'eraser') { if (s.points.length < 2) return; ctx.beginPath(); ctx.moveTo(s.points[0].x, s.points[0].y); s.points.forEach(p => ctx.lineTo(p.x, p.y)); ctx.stroke(); }
            else if (s.tool === 'line') { ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke(); }
            else if (s.tool === 'arrow') { const a = Math.atan2(s.y2-s.y1, s.x2-s.x1), hl=15; ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke(); ctx.beginPath(); ctx.moveTo(s.x2, s.y2); ctx.lineTo(s.x2-hl*Math.cos(a-Math.PI/6), s.y2-hl*Math.sin(a-Math.PI/6)); ctx.moveTo(s.x2, s.y2); ctx.lineTo(s.x2-hl*Math.cos(a+Math.PI/6), s.y2-hl*Math.sin(a+Math.PI/6)); ctx.stroke(); }
            else if (s.tool === 'rect') { ctx.strokeRect(s.x1, s.y1, s.x2-s.x1, s.y2-s.y1); }
            else if (s.tool === 'circle') { const rx=Math.abs(s.x2-s.x1)/2, ry=Math.abs(s.y2-s.y1)/2; ctx.beginPath(); ctx.ellipse((s.x1+s.x2)/2, (s.y1+s.y2)/2, rx||1, ry||1, 0, 0, Math.PI*2); ctx.stroke(); }
        });
    }, [sketch]);
    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-[98vw] max-w-[900px] bg-[#0f172a] border-[#334155] text-white p-0">
                <div className="flex items-center justify-between px-3 py-2 border-b border-[#334155]">
                    <DialogTitle className="text-sm sm:text-base font-semibold">{sketch.title || 'Situatieschets'}</DialogTitle>
                    <Button size="sm" variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white w-8 h-8 p-0"><X size={18} /></Button>
                </div>
                <div className="p-2 sm:p-3">
                    <div className="relative rounded-lg overflow-hidden border border-[#334155] mx-auto" style={{ width: '100%', maxWidth: 800, aspectRatio: '800/500' }}>
                        <RoadBg type={sketch.background} w={800} h={500} />
                        <canvas ref={canvasRef} width={800} height={500} className="absolute inset-0 z-10 w-full h-full" style={{ pointerEvents: 'none' }} />
                        {(sketch.elements || []).map((car, i) => (
                            <div key={i} className="absolute z-20" style={{ left: `${car.x}%`, top: `${car.y}%`, transform: `translate(-50%,-50%) rotate(${car.rotation||0}deg)` }}>
                                <svg width="44" height="24" viewBox="0 0 36 20">
                                    <rect x="1" y="4" width="34" height="12" rx="3" fill={car.color}/>
                                    <rect x="6" y="1" width="12" height="10" rx="2" fill={car.color} opacity="0.7"/>
                                    <rect x="8" y="2" width="8" height="6" rx="1" fill="#87CEEB" opacity="0.6"/>
                                    <circle cx="9" cy="17" r="3" fill="#333"/><circle cx="27" cy="17" r="3" fill="#333"/>
                                </svg>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
